import { getPhasePosition } from '@/allocation/allocation';
import {
    getUnstyledAllocationVariantIndex,
    getUnstyledAllocationVariantLabel,
} from '@/components/AllocationVariantIndex/getFullAllocationVariantIndex';
import { DEFAULT_HIGHLIGHT_VALUE } from '@/components/AllocationVariantSelectionPanel/types';
import { BadgeDropDownValue } from '@/components/BadgeDropDown/BadgeDropDown.vue';
import { mapToPhaseColor } from '@/components/Timeline/PhaseColorMappings';
import { Allocation, LocationTypeCategory, PhaseTypeDatabase } from '@/components/Timeline/Timeline.types';
import { mapStringToSpecializationType } from '@/components/Timeline/allocation/SpecializationMappings';
import { TimelineVariant } from '@/components/Timeline/allocation/types';
import { getDurationInDays } from '@/components/Timeline/logic/durations';
import { getTransparencyVariant } from '@/components/Timeline/timeline';
import {
    ExternalEventAllocationUsageType,
    ProjectBaseDataFragment,
    ProjectVariantDataFragment,
    TimelineProjectAllocationAreaFragment,
    TimelineProjectAllocationRoomFragment,
    TimelineProjectVariantFragment,
} from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import router from '@/plugins/router';
import { insideRangeCheck } from '@/utils/date';
import { getVariantRoute } from '@/variant/utils';
import { DateTime } from 'luxon';
import {
    ProjectSeriesListItem,
    ProjectSeriesStatus,
    ProjectStatus,
    ProjectVariantStatus,
    projectStates,
} from './types';

export const DEFAULT_PROJECT_STATUS = 'active-option';

export function getCreateProjectLink(seriesId: string, seriesName: string) {
    return { name: 'project-add', query: { seriesId, seriesName } };
}

export function getCreateProjectSeriesLink() {
    return { name: 'project-series-add' };
}

export function getProjectSeriesLink(seriesId: string) {
    return { name: 'project-series-overview-tab', params: { seriesId } };
}

export function goToProjectSeries(seriesId: string) {
    const to = getProjectSeriesLink(seriesId);
    router.push(to);
}

export function getProjectLink(projectId: string) {
    return { name: 'project-details-tab', params: { projectId } };
}

export function goToProject(projectId: string) {
    const to = getProjectLink(projectId);
    router.push(to);
}

export function getProjectAllocationsRoute(projectId: string) {
    return { name: 'project-overview-tab', params: { projectId } };
}

export function getSeriesStatus(projectStates: string[]): ProjectSeriesStatus | undefined {
    if (!projectStates.length) return undefined;
    const activeStates = ['active-option', 'active-fix', 'active-idea'];
    if (projectStates.some((status) => activeStates.includes(status))) return 'active';
    if (projectStates.some((status) => status === 'completed')) return 'completed';
    return 'discontinued';
}

export function getProjectSeriesStatusLabel(status: string) {
    return $t('project-series-status', { status });
}

export function getProjectStatusLabel(status: string) {
    return $t('project-status', { status });
}

export function getProjectVariantStatusLabel(status: string) {
    return $t('project-variant-status', { status });
}

export function getSeriesBadgeLabel(series: ProjectSeriesListItem) {
    const status = getSeriesStatus(series.projects?.nodes.map((p) => p.status) ?? []);
    if (status) return getProjectSeriesStatusLabel(status);
    return '';
}

export function projectSeriesStatusToBadgeVariant(status: string) {
    switch (status as ProjectSeriesStatus) {
        case 'active':
            return 'Green';
        case 'discontinued':
            return 'Red';
        case 'completed':
            return 'Gray';
        default:
            return 'Green';
    }
}

export function getSeriesBadgeVariant(series: ProjectSeriesListItem) {
    const status = getSeriesStatus(series.projects?.nodes.map((p) => p.status) ?? []);
    if (status) return projectSeriesStatusToBadgeVariant(status);
    return 'Green';
}

export function projectStatusToBadgeVariant(status: string) {
    switch (status as ProjectStatus) {
        case 'active-option':
            return 'Green';
        case 'active-fix':
            return 'Green';
        case 'active-idea':
            return 'Green';
        case 'completed':
            return 'Gray';
        case 'discontinued':
            return 'Red';
        default:
            return 'Green';
    }
}

export function getProjectStatusItems(status: ProjectStatus): (BadgeDropDownValue & { isSelected: boolean })[] {
    return projectStates.map((s) => ({
        id: s,
        label: getProjectStatusLabel(s),
        variant: projectStatusToBadgeVariant(s),
        isSelected: status === s,
    }));
}

export function getProjectStatusComboboxItems() {
    return projectStates.map((s) => ({
        id: s,
        name: getProjectStatusLabel(s),
    }));
}

export function getProjectStatusItem(status: ProjectStatus): BadgeDropDownValue {
    return { id: status, label: getProjectStatusLabel(status), variant: projectStatusToBadgeVariant(status) };
}

export function projectVariantStatusToBadgeVariant(status: string) {
    switch (status as ProjectVariantStatus) {
        case 'active-1':
            return 'Green';
        case 'active-2':
            return 'Green';
        case 'passive':
            return 'Yellow';
        case 'discontinued':
            return 'Red';
    }
}

export function getProjectVariantRoute(projectId: string, variantIndex: number) {
    return { name: 'project-variants-tab', params: { projectId }, hash: `#v${variantIndex}` };
}

export function getProjectVariantLinkTitle(project: ProjectBaseDataFragment, variant: ProjectVariantDataFragment) {
    const index = getUnstyledAllocationVariantIndex(variant.index);
    if (variant.name === '') {
        return `${index} - ${project.nameShort} - ${project.series?.nameShort}`;
    }
    return `${index} (${variant.name}) - ${project.nameShort} - ${project.series?.nameShort}`;
}

export type TimelineProjectVariantAllocationData = {
    id: string;
    index: number;
    name: string;
    referenceAllocationRoomId: string | null;
    referenceAllocationAreaId: string | null;
    isCurrent: boolean;
    project: {
        id: string;
        nameShort: string;
    };
};

export function mapProjectAllocationForTimeline(
    variant: TimelineProjectVariantAllocationData,
    allocation: TimelineProjectAllocationRoomFragment | TimelineProjectAllocationAreaFragment,
    locationTypeCategory: LocationTypeCategory,
    executionPhasePosition: number,
    areAllocationsEditable: boolean,
): Allocation {
    const referenceAllocationId = variant.referenceAllocationRoomId ?? variant.referenceAllocationAreaId ?? null;
    const allocationStart = DateTime.fromISO(allocation.start);
    const allocationEnd = DateTime.fromISO(allocation.end);

    return {
        id: allocation.id,
        allocationType: 'Project',
        allocationVariant: {
            id: variant.id,
            index: variant.index,
            label: variant.name,
            target: getVariantRoute('Project', variant.project.id, variant.index),
            referenceAllocationId,
        },
        label: $t('allocation-bar-label', {
            groupLabel: variant.project.nameShort,
            variantLabel: getUnstyledAllocationVariantLabel(variant.index, variant.name),
        }),
        variantLabel: getUnstyledAllocationVariantLabel(variant.index, variant.name),
        groupLabel: variant.project.nameShort,
        phases: allocation.phases.nodes.map((phase) => {
            const offsetDays = getDurationInDays(phase.offset);
            const durationDays = getDurationInDays(phase.duration);
            return {
                id: phase.id,
                startDateTime: allocationStart.plus({ days: offsetDays }),
                endDateTime: allocationStart.plus({ days: offsetDays + durationDays }),
                cellSpan: durationDays,
                position: phase.usage.isExecution
                    ? 'Main'
                    : getPhasePosition(executionPhasePosition, phase.usage.position),
                specialization: mapStringToSpecializationType(phase.specializationType),
                offset: phase.offset,
                duration: phase.duration,
                color: mapToPhaseColor(
                    phase.usage.type as Exclude<PhaseTypeDatabase, ExternalEventAllocationUsageType>,
                ),
                usage: {
                    id: phase.usage.id,
                    type: phase.usage.type as PhaseTypeDatabase,
                    position: phase.usage.position,
                },
            };
        }),
        startDateTime: allocationStart,
        endDateTime: allocationEnd,
        locationId: allocation.locationId,
        clickAction: areAllocationsEditable
            ? { type: 'Edit' as const }
            : {
                  type: 'Open-Url' as const,
                  tooltipText: $t('click-to-route-to-masterdata'),
                  target: getProjectVariantRoute(variant.project.id, variant.index),
              },
        linkTarget: getProjectLink(variant.project.id),
        isOptional: allocation.isOptional,
        variant: getTransparencyVariant(DEFAULT_HIGHLIGHT_VALUE, variant.id, variant.isCurrent),
        isInside: locationTypeCategory === 'Room' ? true : false,
        locationTypeCategory,
    };
}

export function mapProjectVariantForTimeline(
    variant: TimelineProjectVariantFragment,
    timelineStart: DateTime | undefined,
    timelineEnd: DateTime | undefined,
    executionPhasePosition: number,
    areAllocationsEditable: boolean,
): TimelineVariant {
    const mappedVariant = {
        ...variant,
        referenceAllocationRoomId: variant.referenceAllocationRoomId ?? null,
        referenceAllocationAreaId: variant.referenceAllocationAreaId ?? null,
    };
    const allocationsTotalCount = variant.rooms.totalCount + variant.areas.totalCount;
    if (timelineStart && timelineEnd) {
        return {
            ...variant,
            referenceAllocationId: variant.referenceAllocationAreaId ?? variant.referenceAllocationRoomId ?? null,
            status: variant.status as ProjectVariantStatus,
            allocationsTotalCount,
            allocations: {
                nodes: [
                    ...(variant.rooms?.nodes
                        .filter((n) =>
                            insideRangeCheck(
                                DateTime.fromISO(n.start),
                                DateTime.fromISO(n.end),
                                timelineStart,
                                timelineEnd,
                            ),
                        )
                        .map((n) =>
                            mapProjectAllocationForTimeline(
                                mappedVariant,
                                n,
                                'Room',
                                executionPhasePosition,
                                areAllocationsEditable,
                            ),
                        ) ?? []),
                    ...(variant.areas?.nodes
                        .filter((n) =>
                            insideRangeCheck(
                                DateTime.fromISO(n.start),
                                DateTime.fromISO(n.end),
                                timelineStart,
                                timelineEnd,
                            ),
                        )
                        .map((n) =>
                            mapProjectAllocationForTimeline(
                                mappedVariant,
                                n,
                                'Area',
                                executionPhasePosition,
                                areAllocationsEditable,
                            ),
                        ) ?? []),
                ],
            },
        };
    }
    return {
        ...variant,
        referenceAllocationId: variant.referenceAllocationAreaId ?? variant.referenceAllocationRoomId ?? null,
        status: variant.status as ProjectVariantStatus,
        allocationsTotalCount,
        allocations: {
            nodes: [
                ...(variant.rooms?.nodes.map((n) =>
                    mapProjectAllocationForTimeline(
                        mappedVariant,
                        n,
                        'Room',
                        executionPhasePosition,
                        areAllocationsEditable,
                    ),
                ) ?? []),
                ...(variant.areas?.nodes.map((n) =>
                    mapProjectAllocationForTimeline(
                        mappedVariant,
                        n,
                        'Area',
                        executionPhasePosition,
                        areAllocationsEditable,
                    ),
                ) ?? []),
            ],
        },
    };
}
